import {
  OrderFreightType,
  OrderFreightTypeMap,
  OrderStopTimeTypeMap,
  OrderStopType,
  OrderStopTypeMap,
} from "@/data/order";
import type { OrderDto, OrderFreightDto, OrderStopDto } from "@/api/orders/dto/order.dto";
import { getCurrentInstance } from "vue";
import { useDateTime } from "@/composables";
import type { TripOrder, TripOrderStop, TripReloadStop, TripRouteFreight } from "@/views/trips/edit/trip";
import { customAlphabet } from "nanoid";
import type { TripMonitorRouteStop } from "@/api/trips/dto/trip-monitor";
import i18 from "@/plugins/i18n";

const t = i18.global.t;

export function useOrder() {
  const { getTZNames } = useDateTime();

  const STOP_DELIVERY = OrderStopType.delivery;
  const STOP_PICK_UP = OrderStopType.pickup;
  const STOP_RELOAD = OrderStopType.reload;

  const freightTypePallets = "pl";
  const freightTypePieces = "pc";

  const timeTypesMap: { [key: string]: string } = {
    ap: "Appointment",
    as: "ASAP",
    d: "Direct",
    f: "FCFS",
  };

  const certificates = ["Hazmat", "Tanker", "TSA", "TWIC"];

  function getOrderRoute(order: OrderDto | TripOrder): string {
    let firstPickup: OrderStopDto | null = null;
    let lastDelivery: OrderStopDto | null = null;

    const stops = "tripStops" in order ? order.tripStops : order.orderStops;

    for (const stop of stops) {
      if (stop.type === STOP_PICK_UP && firstPickup === null) {
        firstPickup = stop;
      }
      if (stop.type === STOP_DELIVERY) {
        lastDelivery = stop;
      }
    }

    return `${firstPickup?.facility?.address} → ${lastDelivery?.facility?.address}`;
  }

  function getStopDistance(
    stops: OrderStopDto[],
    distances: {
      [key: string]: number;
    },
    orderIndex: number,
  ): string {
    const distanceKey = `${stops[orderIndex].id}-${stops[orderIndex + 1].id}`;
    const distanceInMiles = distances[distanceKey] || 0;
    return getCurrentInstance()?.appContext.config.globalProperties.$filters.miles(distanceInMiles) || "";
  }

  function getStopTime(
    stop: OrderStopDto | TripMonitorRouteStop,
    { showFirstTime = true, showSecondTime = true } = {},
  ): string {
    const times = [];
    if (showFirstTime && stop.timeTo) {
      times.push(getTimeInterval(stop.timeFrom!, stop.timeTo, stop.timezone));
    }
    if (showSecondTime && stop.time2To) {
      times.push(getTimeInterval(stop.time2From!, stop.time2To, stop.timezone));
    }
    return times.join(", ");
  }

  function getTimeType(id: string) {
    return OrderStopTimeTypeMap.find((t) => t.id === id)?.name;
  }

  function getTimeInterval(from: string | undefined, to: string | undefined, timezone: string): string {
    const tzShort = getTZNames(timezone);
    const intervals = [];

    if (from) intervals.push(getDateToDisplay(from, timezone));
    if (to) intervals.push(getDateToDisplay(to, timezone));
    if (intervals.length === 0) return "";
    if (intervals.length === 2) {
      if (intervals[0].date === intervals[1].date) {
        return `${intervals[0].date} ${intervals[0].time} - ${intervals[1].time} ${tzShort}`;
      }
    }

    return intervals.map((d) => `${d.date} ${d.time}`).join(" - ") + ` ${tzShort}`;
  }

  function getDateToDisplay(date: string, timezone: string) {
    const dayjs = getCurrentInstance()?.appContext.config.globalProperties.$dayjs;
    const dateTz = dayjs(date).tz(timezone);
    return {
      date: dateTz.format("MM/DD/YYYY"),
      time: dateTz.format("HH:mm"),
    };
  }

  function getTimeTypes(type: OrderStopType) {
    const keys = Object.keys(timeTypesMap);
    return (type === OrderStopType.pickup ? keys.filter((t) => t !== "d") : keys.filter((t) => t !== "as")).map(
      (k) => ({
        id: k,
        name: timeTypesMap[k],
      }),
    );
  }

  function getFreightName(
    freight: TripRouteFreight | OrderFreightDto,
    number: number | null = null,
    firstName = "Freight",
    withStackable = true,
    asPlanned = false,
  ): string {
    const nameParts = [firstName, number ? `#${number}:` : ""];

    if (asPlanned) {
      if ("plannedQuantity" in freight && freight.plannedQuantity) {
        nameParts.push(freight.plannedQuantity.toString(), freight.type === "pl" ? "pallets" : "pieces");
      }
      if ("plannedWeight" in freight && freight.plannedWeight) {
        nameParts.push(freight.plannedWeight.toString(), "lbs");
      }
    } else {
      if (freight.quantity) {
        nameParts.push(freight.quantity.toString(), freight.type === "pl" ? "pallets" : "pieces");
      }
      if (freight.weight) {
        nameParts.push(freight.weight.toString(), "lbs");
      }
    }

    if (freight.length && freight.width && freight.height) {
      nameParts.push(`${freight.length}x${freight.width}x${freight.height}`);
    }

    if (!freight.stackable && withStackable) {
      nameParts.push("Not stackable");
    }

    return nameParts.join(" ").trim();
  }

  function getFreightTypeName(freight: OrderFreightDto) {
    return OrderFreightTypeMap.find((ft) => ft.id === freight.type)?.name;
  }

  function isReloadStop(stop: TripOrderStop | TripReloadStop): stop is TripReloadStop {
    return Boolean(stop.isReload);
  }

  function createOrderFreightFromRouteFreight(routeFreight: TripRouteFreight): OrderFreightDto {
    return {
      id: routeFreight.id,
      name: routeFreight.name,
      number: routeFreight.number,
      type: routeFreight.type,
      isDeleted: routeFreight.isDeleted,
      height: routeFreight.height,
      weight: routeFreight.plannedWeight,
      length: routeFreight.length,
      quantity: routeFreight.plannedQuantity,
      state: routeFreight.state,
      stackable: routeFreight.stackable,
    };
  }

  function generateId() {
    const nanoid = customAlphabet("1234567890abcdef", 16);
    return nanoid();
  }

  function createStopName(type: OrderStopType, number: number, suffix = ""): string {
    return `${OrderStopTypeMap[type]} ${suffix} #${number}`;
  }

  function calculateTotalFreights(freights: OrderFreightDto[]): string {
    const totals = freights
      .filter((f) => !f.isDeleted)
      .reduce(
        (acc, f) => {
          if (f.type === OrderFreightType.pallets && f.quantity) {
            acc.plt += f.quantity;
          }
          if (f.type === OrderFreightType.pieces && f.quantity) {
            acc.pcs += f.quantity;
          }
          if (f.weight) {
            acc.lbs += f.weight;
          }
          return acc;
        },
        { plt: 0, pcs: 0, lbs: 0 },
      );

    const parts: string[] = [];
    if (totals.plt) {
      parts.push(`${totals.plt} plt`);
    }
    if (totals.pcs) {
      parts.push(`${totals.pcs} pcs`);
    }
    if (totals.lbs) {
      parts.push(`${totals.lbs} lbs`);
    }

    return parts.join(", ");
  }

  function createTOName(index: number, orderNumber: number | undefined, prefix = t("general.travel-order")) {
    return `${prefix} #${index + 1} (${t("general.order")}#${orderNumber})`;
  }

  return {
    STOP_DELIVERY,
    STOP_PICK_UP,
    STOP_RELOAD,
    freightTypePallets,
    freightTypePieces,
    timeTypesMap,
    certificates,
    getOrderRoute,
    getStopDistance,
    getStopTime,
    getTimeType,
    getTimeInterval,
    getDateToDisplay,
    getTimeTypes,
    getFreightName,
    getFreightTypeName,
    isReloadStop,
    createOrderFreightFromRouteFreight,
    generateId,
    createStopName,
    calculateTotalFreights,
    createTOName,
  };
}
